import { useChartContext } from '../core/chart-context';
import type { ChartTooltipState, TooltipEntry } from '../core/use-chart-tooltip';

export interface ChartTooltipBodyProps {
  title: string;
  entries: TooltipEntry[];
}

/** Tooltip content — title row plus one swatch/label/value row per entry. */
export const ChartTooltipBody = ({ title, entries }: ChartTooltipBodyProps) => (
  <>
    <div className='text-foreground-muted'>{title}</div>
    {entries.map(entry => (
      <div key={entry.label} className='flex items-center gap-xs text-foreground-base'>
        <span
          aria-hidden
          className='size-2 shrink-0 rounded-full'
          style={{ backgroundColor: entry.color }}
        />
        <span className='truncate'>{entry.label}</span>
        <span className='ml-auto font-medium'>{entry.value}</span>
      </div>
    ))}
  </>
);

ChartTooltipBody.displayName = 'ChartTooltipBody';

/** Shared container classes for the floating chart tooltip. */
export const CHART_TOOLTIP_CLASS =
  'pointer-events-none absolute z-10 min-w-24 rounded-lg border border-border-base bg-background-input px-sm py-xs text-xs shadow-sm';

export interface ChartTooltipProps {
  state: ChartTooltipState | null;
}

/** Floating tooltip positioned over the chart, styled with Tappeer tokens. */
export const ChartTooltip = ({ state }: ChartTooltipProps) => {
  const { dimensions } = useChartContext();
  if (!state) return null;

  const left = dimensions.margin.left + state.x;
  const top = dimensions.margin.top + state.y;
  const flip = left > dimensions.width / 2;

  return (
    <div
      data-slot='chart-tooltip'
      role='tooltip'
      className={CHART_TOOLTIP_CLASS}
      style={{
        left,
        top,
        transform: `translate(${flip ? 'calc(-100% - 8px)' : '8px'}, -50%)`
      }}
    >
      <ChartTooltipBody title={state.title} entries={state.entries} />
    </div>
  );
};

ChartTooltip.displayName = 'ChartTooltip';
